import React, { useState, useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Button from "Components/button/Button";
import ResponsiveTable from "Components/responsiveTable/ResponsiveTable";
import EmailAccountRow from "Components/emailAccountRow/EmailAccountRow";
import FullPaginationControls from "Components/fullPaginationControls/FullPaginationControls";
import TranslatedString from "Components/TranslatedString";
import withConfirmationModal from "Components/withConfirmationModal";
import Modal from "Components/smallModal/smallModal";
import TextArea from "Components/textArea/TextArea";
import { Icon, addEmailAccount, plus, mail, warningFill } from "Icons/index";
import DomainStatusBadge from "Components/domainStatusBadge/DomainStatusBadge";
import qs from "query-string";
import { t } from "i18next";
import s from "./listEmailAccounts.scss";
import { MediaQueriesContext, AuthContext } from "../../App";
import { useApiMutation, useApiQuery } from "../../hooks";

const DEFAULT_BOUNCE_MSG = "This address is disabled and no longer accepts mail.";

const EmailAccountsListScreen = ({ match, openConfirmationModal }) => {
  const history = useHistory();
  const location = useLocation();

  const { isMobileDevice, isMediaMobile } = useContext(MediaQueriesContext);
  const { getLoggedUser, isLoggedUserAdmin } = useContext(AuthContext);

  const loggedUser = getLoggedUser();
  const emailCode = match.params.emailCode || loggedUser?.code;
  const domainCode = match.params.domainCode || loggedUser?.domain_code;

  const [state, setState] = useState({
    domainCode,
    emailCode,
    customerCode: match.params.customerCode,
    timeout: undefined,
    hasArchiveModalOpened: false,
    disableModalOpened: false,
    enableModalOpened: false,
    bounceMsg: undefined,
    selectedEmailCode: undefined,
    selectedEmail: undefined,
  });

  const {
    data: emailAccounts,
    isLoading: areEmailAccountsLoading,
    pagination: paginationEmailAccountsInfo,
    isRefreshing: isEmailAccountsRefreshing,
    refreshData: refreshEmailAccounts,
    getData: fetchEmailAccounts,
  } = useApiQuery("getDomainsEmailAccounts", {
    requestParams: state.domainCode,
    enablePagination: true,
    autoRefresh: {
      shouldRefresh: () => emailAccounts.some(({ status }) => status === "updating"),
      interval: 4000,
    },
  });
  const { mutate: changeEmailAccountStatus } = useApiMutation("changeEmailAccountStatus");
  const { mutate: deleteEmailAccount } = useApiMutation("deleteEmailAccount");

  const addNew = () => {
    const user = getLoggedUser();
    const { customerCode, domainCode } = state;

    const urlByType = {
      Admin: `/customers/${customerCode}/domains/${domainCode}/email_accounts/add`,
      Postmaster: "/add",
      default: `/domains/${domainCode}/email_accounts/add`,
    };

    history.push(urlByType[user?.type] || urlByType.default);
  };

  const handleChangeEmailAccountStatus = async (code, status) => {
    try {
      await changeEmailAccountStatus(state.domainCode, code, status, state.bounceMsg);
      status === "enabled" ? closeEnableModal() : closeDisableModal();
      refreshEmailAccounts(state.domainCode);
    } catch (err) {
      //!TODO Gestione errori migliore magari notificando l'utente
      console.error(err);
    }
  };

  const deleteEmail = async (codeEmail) => {
    try {
      await deleteEmailAccount(state.domainCode, codeEmail);
      refreshEmailAccounts(state.domainCode);
    } catch (err) {
      //!TODO Gestione errori migliore magari notificando l'utente
      console.error(err);
    }
  };

  const openHasArchiveModal = () => {
    setState((prev) => ({ ...prev, hasArchiveModalOpened: true }));
  };

  const closeHasArchiveModal = () => {
    setState((prev) => ({ ...prev, hasArchiveModalOpened: false }));
  };

  const openDisableModal = (emailCode, selectedEmail) => {
    setState((prev) => ({
      ...prev,
      disableModalOpened: true,
      selectedEmailCode: emailCode,
      selectedEmail,
    }));
  };

  const closeDisableModal = () => {
    setState((prev) => ({
      ...prev,
      disableModalOpened: false,
      selectedEmailCode: undefined,
      selectedEmail: undefined,
      bounceMsg: undefined,
    }));
  };

  const openEnableModal = (emailCode, selectedEmail) => {
    setState((prev) => ({
      ...prev,
      enableModalOpened: true,
      selectedEmailCode: emailCode,
      selectedEmail,
    }));
  };

  const closeEnableModal = () => {
    setState((prev) => ({
      ...prev,
      enableModalOpened: false,
      selectedEmailCode: undefined,
      bounceMsg: undefined,
    }));
  };

  const openDeleteModal = (em) => {
    if (em.archive) {
      openHasArchiveModal();
    } else {
      openConfirmationModal(
        em,
        deleteEmail,
        <TranslatedString subs={{ emailAccount: em.email_address }}>modal.delete.emailAccount.text</TranslatedString>
      );
    }
  };

  const updateBounceMsg = (val) => {
    setState((prev) => ({ ...prev, bounceMsg: val.bounce_msg }));
  };

  const { hasArchiveModalOpened, disableModalOpened, enableModalOpened, selectedEmail } = state;

  let canAddEmailAccount = true;
  if (loggedUser?.type === "TeamMember" && !loggedUser.permissions?.includes("create_email_accounts")) {
    canAddEmailAccount = false;
  }

  const query = qs.parse(location.search);

  return (
    <div className={s.listEmailAccounts}>
      <div className={s.wrapInfoBtn}>
        {!isMediaMobile && (
          <div className={s.infoText}>
            <TranslatedString>domains.show.desc.emailAccounts</TranslatedString>
          </div>
        )}

        {canAddEmailAccount && (
          <Button className={s.addBtn} icon={plus} onClick={addNew}>
            button.addEmail
          </Button>
        )}
      </div>
      <FullPaginationControls
        className={s.filters}
        paginationInfo={paginationEmailAccountsInfo}
        location={location}
        refreshList={() => fetchEmailAccounts(state.domainCode)}
        listFilters={["all", "enabled", "disabled", "failure", "updating"]}
        listFilterLegends={{
          all: <Icon svg={mail} width="0.75rem" height="0.75rem" />,
          enabled: <DomainStatusBadge status="enabled" isHealthy showAsPreview />,
          disabled: <DomainStatusBadge status="disabled" isHealthy showAsPreview />,
          failure: <DomainStatusBadge status="failure" isHealthy showAsPreview />,
          updating: <DomainStatusBadge status="updating" isHealthy showAsPreview />,
        }}
        isLoading={areEmailAccountsLoading || isEmailAccountsRefreshing}
        totals={paginationEmailAccountsInfo.totals}
      />

      <div className={s.table}>
        <ResponsiveTable
          history={history}
          areRowsLoading={areEmailAccountsLoading && !isEmailAccountsRefreshing}
          location={location}
          addIcon={addEmailAccount}
          addMsg={t("table.row.add.msg.emailAccout")}
          addDesc={t("table.row.add.desc.emailAccout")}
          headers={[
            {
              name: t("table.header.status"),
              size: isMobileDevice ? "2rem" : "4rem",
              center: true,
            },
            {
              name: t("table.header.account"),
              key: "name",
              sortable: true,
              activeSorting: query.sort_field === "name" && query.sort,
            },
            {
              name: t("table.header.quote"),
              size: isMediaMobile ? "3rem" : "14rem",
              sortable: true,
              key: "quota",
              activeSorting: query.sort_field === "quota" && query.sort,
            },
            {
              name: t("table.header.lastAccess"),
              size: "11rem",
              hideOnMobile: true,
              sortable: true,
              key: "lastauth",
              activeSorting: query.sort_field === "lastauth" && query.sort,
            },
            {
              name: t("table.header.messagesIn24H"),
              size: "7rem",
              hideOnMobile: true,
            },
            { size: "3rem", center: true },
          ]}
          rows={emailAccounts.map((em) => {
            if (em.name === "postmaster") return undefined;
            return (
              <EmailAccountRow
                key={em.code}
                emailAccount={em}
                isLoggedUserAdmin={isLoggedUserAdmin()}
                location={location}
                deleteEmail={() => openDeleteModal(em)}
                openDisableModal={openDisableModal}
                openEnableModal={openEnableModal}
              />
            );
          })}
        />
      </div>

      <Modal
        className={s.modaNoAction}
        opened={hasArchiveModalOpened}
        closeModal={closeHasArchiveModal}
        title={t("modal.noAction.title")}
      >
        <div>
          <TranslatedString>modal.withArchive.emailAccount</TranslatedString>
        </div>
        <div className={s.footerModaNoAction}>
          <Button onClick={closeHasArchiveModal} type="button" color="outline">
            button.close
          </Button>
        </div>
      </Modal>

      <Modal
        title={<TranslatedString>modal.enable.emailAccount.title</TranslatedString>}
        icon={warningFill}
        iconClassName={s.iconEnableModal}
        opened={enableModalOpened}
        closeModal={closeEnableModal}
      >
        <div>
          <TranslatedString subs={{ emailAccount: selectedEmail }}>modal.enable.emailAccount.text</TranslatedString>
        </div>
        <div>
          <Button onClick={closeEnableModal} type="button" color="text">
            button.cancel
          </Button>
          <Button onClick={() => handleChangeEmailAccountStatus(state.selectedEmailCode, "enabled")} type="button">
            button.enable
          </Button>
        </div>
      </Modal>

      <Modal
        className={s.disableModal}
        title={<TranslatedString>modal.disable.emailAccount.title</TranslatedString>}
        icon={warningFill}
        iconClassName={s.iconDisableModal}
        opened={disableModalOpened}
        closeModal={closeDisableModal}
      >
        <div>
          <TranslatedString subs={{ emailAccount: selectedEmail }}>modal.disable.emailAccount.text</TranslatedString>

          <TextArea
            className={s.textarea}
            name="bounce_msg"
            label={t("input.label.bounceMsg")}
            value={state.bounceMsg || DEFAULT_BOUNCE_MSG}
            onChange={updateBounceMsg}
            required
          />
        </div>
        <div>
          <Button onClick={closeDisableModal} type="button" color="text">
            button.cancel
          </Button>
          <Button
            onClick={() => handleChangeEmailAccountStatus(state.selectedEmailCode, "disabled")}
            type="button"
            color="red"
          >
            button.disable
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default withConfirmationModal(EmailAccountsListScreen, {
  title: <TranslatedString>modal.delete.emailAccount.title</TranslatedString>,
  nameAttribute: "email_address",
});
